import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../stores/authStore';
import type { PartyProfile } from '../../../stores/authStore';
import { maskIdCard } from '../mask';

interface Props {
  profile: PartyProfile;
  onSaved: () => void;
}

type FormState = {
  fullName: string;
  username: string;
  idCard: string;
  phone: string;
  backupPhone: string;
  companyName: string;
  location: string;
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
  introduction: string;
};

const inputCls = 'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 bg-white text-gray-800 placeholder:text-gray-400 transition-colors';
const labelCls = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';
const PHONE_RE = /^1[3-9]\d{9}$/;

// 备用电话（选填）规则，与注册页 getFieldValueError('backupPhone') 一致
function backupPhoneError(value: string, mainPhone: string): string {
  const v = value.trim();
  if (!v) return '';
  if (!PHONE_RE.test(v)) return '请输入正确的备用电话';
  if (v === mainPhone.trim()) return '备用电话不能与主电话相同';
  return '';
}

export default function BasicInfoForm({ profile, onSaved }: Props) {
  const { updateProfile } = useAuthStore();
  const [form, setForm] = useState<FormState>({
    fullName: '', username: '', idCard: '', phone: '', backupPhone: '',
    companyName: '', location: '', address: '',
    emergencyContact: '', emergencyPhone: '', introduction: '',
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [errMsg, setErrMsg] = useState('');
  const [triedBackupPhone, setTriedBackupPhone] = useState(false);

  useEffect(() => {
    setForm({
      fullName: profile.fullName ?? '',
      username: profile.username ?? '',
      idCard: profile.idCard ?? '',
      phone: profile.phone ?? '',
      backupPhone: profile.backupPhone ?? '',
      companyName: profile.companyName ?? '',
      location: profile.location ?? '',
      address: profile.address ?? '',
      emergencyContact: profile.emergencyContact ?? '',
      emergencyPhone: profile.emergencyPhone ?? '',
      introduction: profile.introduction ?? '',
    });
  }, [profile]);

  const set = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    setStatus('idle');
    if (e.target.name === 'backupPhone') setTriedBackupPhone(false);
  };

  // 校验前置到提交前：错误留在字段下方，不让请求先出门再靠后端回错
  const backupErr = triedBackupPhone ? backupPhoneError(form.backupPhone, form.phone) : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = backupPhoneError(form.backupPhone, form.phone);
    if (err) {
      setTriedBackupPhone(true);
      return;
    }
    setSaving(true);
    setStatus('idle');
    const { error } = await updateProfile({
      phone: form.phone || null,
      backupPhone: form.backupPhone || null,
      companyName: form.companyName || null,
      location: form.location || null,
      address: form.address || null,
      emergencyContact: form.emergencyContact || null,
      emergencyPhone: form.emergencyPhone || null,
      introduction: form.introduction || null,
    });
    setSaving(false);
    if (error) {
      setErrMsg(error.message || '保存失败，请重试');
      setStatus('err');
    } else {
      setStatus('ok');
      onSaved();
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const Section = ({ title }: { title: string }) => (
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2 pb-1 border-b border-gray-100 mb-4">{title}</p>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Section title="基本信息" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>姓名</label>
          <input type="text" value={profile.fullName ?? ''} disabled className={`${inputCls} bg-gray-50 text-gray-400 cursor-not-allowed`} />
          <p className="text-xs text-gray-400 mt-1">姓名不可修改</p>
        </div>
        <div>
          <label className={labelCls}>用户名</label>
          <input type="text" value={profile.username ?? ''} disabled className={`${inputCls} bg-gray-50 text-gray-400 cursor-not-allowed`} />
          <p className="text-xs text-gray-400 mt-1">用户名不可修改</p>
        </div>
        <div>
          <label className={labelCls}>身份证号</label>
          <input type="text" value={maskIdCard(profile.idCard)} disabled className={`${inputCls} bg-gray-50 text-gray-400 cursor-not-allowed tracking-widest`} />
          <p className="text-xs text-gray-400 mt-1">身份证号不可修改</p>
        </div>
        <div>
          <label className={labelCls}>邮箱</label>
          <input type="text" value={profile.email ?? ''} disabled className={`${inputCls} bg-gray-50 text-gray-400 cursor-not-allowed`} />
          <p className="text-xs text-gray-400 mt-1">邮箱不可修改</p>
        </div>
        <div>
          <label className={labelCls}>手机号</label>
          <input type="tel" name="phone" value={form.phone} onChange={set} placeholder="请输入手机号" className={inputCls} />
        </div>
        <div>
          {/* 备用电话：注册即为选填的备用联系方式，文案与注册页同一口径。
              入口在个人资料里，不在账号安全页——它不收验证码，改它不构成安全动作。 */}
          <label className={labelCls}>备用电话（选填）</label>
          <input type="tel" name="backupPhone" value={form.backupPhone} onChange={set} placeholder="主电话无法联系时使用" className={inputCls} />
          {backupErr && <p className="text-xs text-red-500 mt-1">{backupErr}</p>}
        </div>
        <div>
          <label className={labelCls}>所在公司</label>
          <input type="text" name="companyName" value={form.companyName} onChange={set} placeholder="公司名称" className={inputCls} />
        </div>
      </div>

      <Section title="地址信息" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>所在地区</label>
          <input type="text" name="location" value={form.location} onChange={set} placeholder="如：广东省广州市" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>详细地址</label>
          <input type="text" name="address" value={form.address} onChange={set} placeholder="详细地址" className={inputCls} />
        </div>
      </div>

      <Section title="紧急联系人" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>联系人姓名</label>
          <input type="text" name="emergencyContact" value={form.emergencyContact} onChange={set} placeholder="紧急联系人姓名" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>联系人电话</label>
          <input type="tel" name="emergencyPhone" value={form.emergencyPhone} onChange={set} placeholder="紧急联系人电话" className={inputCls} />
        </div>
      </div>

      <Section title="个人简介" />
      <div>
        <textarea
          name="introduction"
          value={form.introduction}
          onChange={set}
          rows={4}
          maxLength={500}
          placeholder="介绍一下自己..."
          className={`${inputCls} resize-none`}
        />
        <p className="text-xs text-gray-400 mt-1 text-right">{form.introduction.length} / 500</p>
      </div>

      {status === 'err' && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
          <i className="ri-error-warning-line" />
          {errMsg}
        </div>
      )}
      {status === 'ok' && (
        <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-100 rounded-lg text-sm text-teal-600">
          <i className="ri-checkbox-circle-line" />
          保存成功！
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          {saving ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />保存中...</span> : '保存修改'}
        </button>
      </div>
    </form>
  );
}
